import { useCallback } from 'react';
import { useGlobalStore } from '@core/hooks/useGlobalStore';
import { playerActionsSelector } from '@core/player/model';
import { EventType, useCustomPlayerVideoEvent, } from '@embed/components/VideoPlayer/Player3/hooks/useCustomPlayerVideoEvent';
import { useVideoEl } from '@embed/components/VideoPlayer/Player3/hooks/useVideoEl';
import { useStore } from '@embed/components/VideoPlayer/Player3/store';
import { useVideoPlayerStore } from '@embed/components/VideoPlayer/store';
import { videoPlayerActionsSelector, videoPlayerIsUserMutedSelector, } from '@embed/components/VideoPlayer/store/selectors';
import { usePlayerTracking } from '@player/tracking/usePlayerTracking';
/**
 * Hook that manage video mute status on behalf of a user by
 * providing `mute`, `unmute` and `toggleMute` callbacks.
 */
export const useMuting = () => {
    const globalPlayerActions = useGlobalStore(playerActionsSelector);
    const videoPlayerStoreActions = useVideoPlayerStore(videoPlayerActionsSelector);
    const videoEl = useVideoEl();
    const isIMAActive = useStore((state) => state.isIMAActive);
    const video = useStore((state) => state.video);
    const isVideoMuted = useStore((state) => state.isVideoMuted);
    const isUserMuted = useVideoPlayerStore(videoPlayerIsUserMutedSelector);
    const { trackMute, trackUnmute } = usePlayerTracking();
    const customPlayerVideoEvent = useCustomPlayerVideoEvent();
    /**
     * Mute video as a user requested action.
     */
    const mute = useCallback(() => {
        globalPlayerActions.setUserMuted?.(true);
        videoPlayerStoreActions.setUserMuted(true);
        videoEl.muted = true;
        trackMute({ video });
        customPlayerVideoEvent({
            type: EventType.FW_VIDEO_MUTE,
        });
    }, [
        customPlayerVideoEvent,
        globalPlayerActions,
        trackMute,
        video,
        videoEl,
        videoPlayerStoreActions,
    ]);
    /**
     * Unmute video on user's behalf as it was his action.
     */
    const unmute = useCallback(() => {
        globalPlayerActions.setUserMuted?.(false);
        videoPlayerStoreActions.setUserMuted(false);
        // Resume video when unmute if video was paused by user.
        // It's because when user shows intent to unmute video, the video must
        // be playing in order to hear the sound.
        if (videoEl.paused && !isIMAActive) {
            videoPlayerStoreActions.setUserPaused(false);
        }
        videoEl.muted = false;
        trackUnmute({ video });
        customPlayerVideoEvent({
            type: EventType.FW_VIDEO_UNMUTE,
        });
    }, [
        customPlayerVideoEvent,
        globalPlayerActions,
        isIMAActive,
        trackUnmute,
        video,
        videoEl,
        videoPlayerStoreActions,
    ]);
    const toggleMute = useCallback(() => {
        if (videoEl.muted) {
            unmute();
        }
        else {
            mute();
        }
    }, [mute, unmute, videoEl]);
    const hasUserInteractedWithMute = typeof isUserMuted !== 'undefined';
    return {
        mute,
        unmute,
        toggleMute,
        isVideoMuted,
        isUserMuted,
        hasUserInteractedWithMute,
    };
};
